import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Checkbox } from '@ovComponents/2-component/checkbox/checkbox';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { Tooltip } from '@ovComponents/2-component/tooltip/tooltip';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { useCallback, useState } from 'react';
import {
  LABEL_SX,
  MICRO_SX,
  SECONDARY_TEXT_SX,
  SMALL_BOLD_SX,
  SMALL_SX,
  TRUNCATE_SX,
} from '../styles';
import { isBuyType, isSellType, tradeSideColor } from '../utils';
import EditableTableCell from './editableTableCell';
import TradeRowDetail from './tradeRowDetail';

const ADVISOR_STATES: Record<
  string,
  {
    label: string;
    badge: 'warning' | 'positive' | 'supportOne' | 'neutral' | 'negative';
    description: string;
  }
> = {
  INITIATED: {
    label: 'Pending',
    badge: 'warning',
    description: 'Awaiting verification or batch eligibility',
  },
  READY: {
    label: 'Ready',
    badge: 'positive',
    description: 'Eligible for next trade batch',
  },
  REQUESTED: {
    label: 'Sent',
    badge: 'supportOne',
    description: 'Submitted to broker, awaiting fill',
  },
  RECONCILED: {
    label: 'Filled',
    badge: 'neutral',
    description: 'Executed and settled',
  },
  CANCELED: {
    label: 'Canceled',
    badge: 'negative',
    description: 'Trade withdrawn',
  },
};

const LOCKED_STATES = new Set(['REQUESTED', 'RECONCILED', 'CANCELED']);

const ActionButton = ({
  label,
  active,
  color,
  onClick,
  sys,
}: {
  label: string;
  active?: boolean;
  color: 'success' | 'error';
  onClick: (e: React.MouseEvent) => void;
  sys: { color: Record<string, string> } | any;
}) => {
  const accent = color === 'success' ? sys.color.positive : sys.color.negative;
  const accentVariant =
    color === 'success' ? sys.color.positiveVariant : sys.color.negativeVariant;
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 0.75,
        py: '3px',
        cursor: 'pointer',
        borderRadius: '4px',
        border: '1px solid',
        borderColor: active ? accent : sys.color.outlineVariant,
        bgcolor: active ? accentVariant : 'transparent',
        transition: 'all 0.12s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        whiteSpace: 'nowrap',
        '&:hover': {
          borderColor: accent,
          bgcolor: accentVariant,
        },
      }}
    >
      {active && color === 'success' && (
        <CheckCircleOutlineIcon sx={{ fontSize: 11, color: accent }} />
      )}
      <Typography
        variant='labelSmall'
        sx={{
          ...LABEL_SX,
          fontWeight: 700,
          color: active ? accent : sys.color.onSurfaceVariant,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const TradeRequestRow = ({
  trade,
  selected,
  onToggleSelect,
  onCancel,
  onVerify,
  onUpdateAmount,
}: {
  trade: any;
  selected?: boolean;
  onToggleSelect?: () => void;
  onCancel?: (id: string) => void;
  onVerify?: (id: string) => void;
  onUpdateAmount?: (
    id: string,
    newAmountCents: number,
    currentPriceCents?: number,
  ) => void;
  isNew?: boolean;
}) => {
  const { sys } = useThemeTokens();
  const [expanded, setExpanded] = useState(false);
  const stateInfo = ADVISOR_STATES[trade.state] || {
    label: trade.state,
    badge: 'neutral' as const,
    description: '',
  };
  const isBuy = isBuyType(trade.type);
  const isSell = isSellType(trade.type, trade.isSellAll);
  const clientName = trade.subAccount?.account?.user?.firstName || 'Unknown';
  const clientLastName = trade.subAccount?.account?.user?.lastName || '';
  const accountType = trade.subAccount?.account?.type || '—';
  const displayAmount = Math.abs(trade.moneyAllocatedCents || 0);
  const quantity = trade.financialProduct?.currentPriceCents
    ? Math.abs(displayAmount) / trade.financialProduct.currentPriceCents
    : 0;
  const daysToSettle = trade.settlementDate
    ? Math.ceil(
        (new Date(trade.settlementDate).getTime() - Date.now()) / 86400000,
      )
    : null;
  const settleUrgent = daysToSettle !== null && daysToSettle <= 0;
  const settleWarning =
    daysToSettle !== null && daysToSettle > 0 && daysToSettle <= 2;
  const hasSchedule = !!trade.schedule;
  const scheduleNextDate = trade.schedule?.nextDate;
  const isScheduleBlocked =
    hasSchedule && scheduleNextDate && new Date(scheduleNextDate) > new Date();
  const isBlocked =
    trade.blockedUntilAccountActive ||
    trade.blockedUntilSubAccountActive ||
    trade.blockedUntil ||
    isScheduleBlocked;
  const blockedReasons: string[] = [];
  if (trade.blockedUntilAccountActive)
    blockedReasons.push('Account is not active/approved');
  if (trade.blockedUntilSubAccountActive)
    blockedReasons.push('Sub-account is not active/approved');
  if (trade.blockedUntil)
    blockedReasons.push(
      `Blocked until ${new Date(trade.blockedUntil).toLocaleDateString()}`,
    );
  if (isScheduleBlocked)
    blockedReasons.push(
      `Illiquid — next trade date: ${new Date(scheduleNextDate).toLocaleDateString()} (${trade.schedule.name})`,
    );

  const isLocked = LOCKED_STATES.has(trade.state);
  const isInBatch = !!trade.bulkTradeRun && trade.state !== 'REQUESTED';
  const isRequested = trade.state === 'REQUESTED';

  const handleAmountSave = useCallback(
    (newValue: number) => {
      if (onUpdateAmount && trade.state === 'INITIATED' && !isInBatch) {
        const newCents = Math.round(Math.abs(newValue));
        if (newCents !== displayAmount) {
          onUpdateAmount(
            trade.id,
            newCents,
            trade.financialProduct?.currentPriceCents,
          );
        }
      }
    },
    [
      onUpdateAmount,
      trade.id,
      trade.state,
      displayAmount,
      trade.financialProduct?.currentPriceCents,
      isInBatch,
    ],
  );
  const isActionable = !isLocked && !isInBatch;
  const settleSx = {
    color: settleUrgent
      ? sys.color.negative
      : settleWarning
        ? sys.color.warning
        : sys.color.onSurfaceVariant,
    fontWeight: settleUrgent || settleWarning ? 700 : 400,
  };

  return (
    <>
      <TableRow
        sx={{
          bgcolor: isBuy
            ? 'success.light'
            : isSell
              ? 'error.light'
              : 'transparent',
          '&:hover': {
            bgcolor: isBuy
              ? 'success.light'
              : isSell
                ? 'error.light'
                : 'action.hover',
          },
          borderLeft: '3px solid',
          borderLeftColor: isBuy
            ? 'success.main'
            : isSell
              ? 'error.main'
              : 'transparent',
          ...(isLocked || isInBatch ? { opacity: 0.7 } : {}),
        }}
      >
        {onToggleSelect && (
          <TableCell width='3%' sx={{ py: 0.5 }}>
            <Checkbox
              checked={!!selected}
              onChange={() => onToggleSelect()}
              disabled={isLocked || isInBatch}
              customStyle={{
                p: 0,
                ...(isLocked || isInBatch ? { opacity: 0.3 } : {}),
              }}
            />
          </TableCell>
        )}
        <TableCell width={onToggleSelect ? '3%' : '4%'} sx={{ py: 0.5 }}>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            label={expanded ? 'Collapse' : 'Expand'}
            size='small'
          >
            {expanded ? (
              <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </TableCell>
        <TableCell width='15%' sx={{ py: 0.5 }}>
          <Box display='flex' alignItems='center' gap={0.5}>
            <Typography variant='bodySmall' weight='bold'>
              {trade.financialProduct?.ticker || 'Unknown'}
            </Typography>
            {isBlocked &&
              (() => {
                const isHardBlock =
                  trade.blockedUntilAccountActive ||
                  trade.blockedUntilSubAccountActive;
                let badgeColor: any;
                let badgeBg: any;
                let badgeLabel: string;
                if (isHardBlock) {
                  badgeColor = sys.color.negative;
                  badgeBg = sys.color.negativeVariant;
                  badgeLabel = 'BLOCKED';
                } else if (isScheduleBlocked) {
                  badgeColor = sys.color.warning;
                  badgeBg = sys.color.warningVariant;
                  badgeLabel = 'SCHEDULED';
                } else {
                  badgeColor = sys.color.warning;
                  badgeBg = sys.color.warningVariant;
                  badgeLabel = 'BLOCKED';
                }
                return (
                  <Tooltip title={blockedReasons.join(' · ')} placement='top'>
                    <Box
                      display='inline-flex'
                      alignItems='center'
                      sx={{
                        px: 0.5,
                        py: '1px',
                        borderRadius: '4px',
                        bgcolor: badgeBg,
                        color: badgeColor,
                        cursor: 'help',
                      }}
                    >
                      <BlockIcon sx={{ fontSize: 11, mr: 0.25 }} />
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...MICRO_SX,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: 'inherit',
                        }}
                      >
                        {badgeLabel}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })()}
          </Box>
          <Typography
            variant='bodySmall'
            sx={{ ...SECONDARY_TEXT_SX, ...TRUNCATE_SX }}
          >
            {trade.financialProduct?.translatedName?.en || ''}
          </Typography>
        </TableCell>
        <TableCell width='5%' number sx={{ py: 0.5 }}>
          <Typography
            variant='bodySmall'
            weight='bold'
            sx={{ color: tradeSideColor(sys.color, isBuy) }}
          >
            {isBuy ? 'BUY' : 'SELL'}
          </Typography>
        </TableCell>
        <TableCell width='4%' sx={{ py: 0.5 }}>
          <Typography
            variant='labelSmall'
            sx={{
              ...SMALL_BOLD_SX,
              fontWeight: 700,
              letterSpacing: '0.03em',
              color: sys.color.onSurfaceVariant,
            }}
          >
            {trade.currency || '—'}
          </Typography>
        </TableCell>
        <TableCell width='7%' number sx={{ py: 0.5 }}>
          <Typography variant='bodySmall'>{quantity.toFixed(2)}</Typography>
          {trade.isSellAll && (
            <Typography
              variant='bodySmall'
              sx={{ color: sys.color.warning, fontWeight: 700 }}
            >
              SELL ALL
            </Typography>
          )}
        </TableCell>
        {onUpdateAmount && !isLocked ? (
          <EditableTableCell
            value={displayAmount}
            onSave={handleAmountSave}
            formatDisplay={(v) => formatMoneyValue(Number(v), '$', 2)}
            editable={trade.state === 'INITIATED' && !isInBatch}
            number
            width='11%'
            sx={{ py: 0.5 }}
          />
        ) : (
          <TableCell width='11%' number sx={{ py: 0.5 }}>
            <Typography variant='bodySmall' weight='bold'>
              {formatMoneyValue(displayAmount, '$', 2)}
            </Typography>
          </TableCell>
        )}
        <TableCell width='13%' sx={{ py: 0.5 }}>
          <Typography variant='bodySmall'>
            {clientName}
            {clientLastName ? ` ${clientLastName}` : ''}
          </Typography>
          <Typography variant='bodySmall' sx={{ ...SECONDARY_TEXT_SX }}>
            {accountType}
          </Typography>
        </TableCell>
        <TableCell width='7%' sx={{ py: 0.5 }}>
          <Typography variant='bodySmall' sx={settleSx}>
            {trade.settlementDate
              ? new Date(trade.settlementDate).toLocaleDateString()
              : '—'}
          </Typography>
          {daysToSettle !== null && (
            <Typography
              variant='labelSmall'
              sx={{
                color: settleUrgent
                  ? sys.color.negative
                  : settleWarning
                    ? sys.color.warning
                    : sys.color.onSurfaceVariant,
              }}
            >
              T{daysToSettle >= 0 ? '+' : ''}
              {daysToSettle}
            </Typography>
          )}
        </TableCell>
        <TableCell width='12%' sx={{ py: 0.5 }}>
          {isRequested ? (
            <Typography
              variant='labelSmall'
              sx={{
                px: 0.75,
                py: '2px',
                borderRadius: '4px',
                bgcolor: sys.color.positiveVariant,
                color: sys.color.positive,
                ...SMALL_BOLD_SX,
                fontWeight: 700,
                display: 'inline-block',
              }}
            >
              Requested
            </Typography>
          ) : isInBatch ? (
            <Typography
              variant='labelSmall'
              sx={{
                px: 0.75,
                py: '2px',
                borderRadius: '4px',
                bgcolor: sys.color.primaryVariant,
                color: sys.color.primary,
                ...SMALL_BOLD_SX,
                fontWeight: 700,
                display: 'inline-block',
              }}
            >
              In Batch
            </Typography>
          ) : isActionable ? (
            <Box display='flex' alignItems='center' gap={0.5} flexWrap='nowrap'>
              {onVerify && (
                <ActionButton
                  label={trade.verifiedAt ? 'Verified' : 'Verify'}
                  active={!!trade.verifiedAt}
                  color='success'
                  onClick={(e) => {
                    e.stopPropagation();
                    onVerify(trade.id);
                  }}
                  sys={sys}
                />
              )}
              {onCancel && (
                <ActionButton
                  label='Cancel'
                  color='error'
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(trade.id);
                  }}
                  sys={sys}
                />
              )}
            </Box>
          ) : (
            <Typography
              variant='labelSmall'
              sx={{ ...SMALL_SX, color: sys.color.onDisabled }}
            >
              —
            </Typography>
          )}
        </TableCell>
      </TableRow>

      {expanded && (
        <TradeRowDetail
          trade={trade}
          colSpan={onToggleSelect ? 10 : 9}
          stateInfo={stateInfo}
          isBuy={isBuy}
          isBlocked={isBlocked}
        />
      )}
    </>
  );
};

export default TradeRequestRow;
